import {
  Component,
  inject,
  computed,
  Signal,
  input,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import {CommonModule} from '@angular/common';

import {map, Observable} from 'rxjs';

import {AppConfigFacade, TabularColumn} from '@selpia/app-config/data-access';
import {OptionsFacade, OptionsUpdateFacade} from '@selpia/options/data-access';
import {DataBatchFacade} from '@selpia/data-batch/data-access';
import {SelectDataPointPipe} from '@selpia/data-batch/ui';
import {FormatByMeasurePipe} from '@selpia/measures/pipes';
import {BarMinichartComponent, InteractionMessageComponent, SvgSpriteIcon} from '@selpia/shared/ui';
import {DataBatchTools, Scroller} from '@selpia/shared/utils';
import {Alignment, Measure, Sentiment} from '@selpia/shared/models';

interface ExtendedTabularColumn extends TabularColumn {
  isGraph?: boolean;
  graphName?: string;
}

@Component({
  selector: 'lib-feature-tabular-summary',
  imports: [
    CommonModule,
    SelectDataPointPipe,
    FormatByMeasurePipe,
    BarMinichartComponent,
    InteractionMessageComponent,
  ],
  templateUrl: './feature-tabular-summary.component.html',
  styleUrl: './feature-tabular-summary.component.scss',
})
export class FeatureTabularSummaryComponent implements AfterViewInit, OnDestroy {

  readonly #appConfigFacade: AppConfigFacade = inject(AppConfigFacade);
  readonly #optionsFacade = inject(OptionsFacade);
  readonly #optionsUpdateFacade = inject(OptionsUpdateFacade);
  readonly #dataBatchFacade = inject(DataBatchFacade);
  readonly #elementRef: ElementRef = inject(ElementRef);
  readonly title = input<string>();

  #scroller: Scroller | null = null;
  canScroll$!: Observable<boolean>;

  @ViewChild('contentScroller')
  contentScrollerElementRef!: ElementRef;

  protected readonly isLoading = this.#dataBatchFacade.isLoading;
  protected readonly chunk = computed(() => this.#dataBatchFacade.dataChunks()?.at(0));
  protected readonly date: Signal<Date | undefined> = this.#appConfigFacade.initialDate;

  protected readonly periodCode = this.#optionsFacade.focusPeriod;

  readonly #config = this.#appConfigFacade.reportConfig;

  readonly viewIcon = SvgSpriteIcon.Eye;

  readonly #tabularSummary = computed(() => this.#config()?.tabularSummary);

  protected readonly columns: Signal<ExtendedTabularColumn[]> = computed(() => {
    const columns = this.#tabularSummary()?.columns;
    if (!columns?.length) {
      return [];
    }
    return this.#addGraphColumns(columns);
  });
  protected readonly measures = computed(() => this.#tabularSummary()?.measures || []);

  protected readonly focusMeasure = this.#optionsFacade.focusMeasure;

  #graphColumns: Signal<ExtendedTabularColumn[]> = computed(() => (this.columns() || []).filter(column => column.isGraph));

  readonly graphMinMax: Signal<Map<string, {min: number, max: number}>> = computed(() => {
    const result = new Map<string, {min: number, max: number}>();
    const graphColumns = this.#graphColumns();
    const chunk = this.chunk();
    const periodCode = this.periodCode();
    const date = this.date();
    const dataPoints = chunk?.dataPoints || [];

    if (!chunk || !periodCode || !dataPoints.length) {
      return result;
    }

    graphColumns.forEach(column => {
      if (column.graphName) {
        let min = Infinity;
        let max = -Infinity;

        column.measuresCodes.forEach(measureCode => {
          if (!measureCode) return;
          try {
            const dataPoint = DataBatchTools.getDataPointFor(dataPoints, {measureCode, periodCode, date});
            const value = dataPoint?.value;
            if (typeof value === 'number') {
              min = Math.min(min, value);
              max = Math.max(max, value);
            }
          } catch (error) {
            console.error(`Error fetching data point for measure ${measureCode}:`, error);
          }
        });

        if (min !== Infinity && max !== -Infinity) {
          result.set(column.graphName, {min, max});
        }
      }
    });

    return result;
  });

  #addGraphColumns(columns: TabularColumn[]): ExtendedTabularColumn[] {
    return columns.reduce((acc, column: TabularColumn) => {
      const columnCopy = {...column};
      const columnHeader = column.columnHeader;
      const isDelta: boolean = columnHeader.toLowerCase().includes('delta');
      if (isDelta) {
        columnCopy.columnHeader = columnHeader.replace('delta', 'Δ').replace('Delta', 'Δ');
      }
      acc.push(columnCopy);
      if (isDelta) {
        acc.push({...columnCopy, columnHeader: '', isGraph: true, graphName: columnCopy.columnHeader});
      }
      return acc;
    }, [] as ExtendedTabularColumn[]);
  }

  setFocusMeasure(focusMeasure: Measure): void {
    this.#optionsUpdateFacade.setOptions({focusMeasure});
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.#initializeScrollListener();
    });
  }

  ngOnDestroy(): void {
    this.#scroller?.destroy();
  }

  #initializeScrollListener() {
    this.#scroller = new Scroller(
      this.contentScrollerElementRef.nativeElement,
      this.#elementRef.nativeElement,
    );
    this.canScroll$ = this.#scroller.canScroll$.pipe(
      map(({left, right}) => left || right),
    );
    setTimeout(() => this.#scroller?.check(), 500);
  }

  protected readonly Sentiment = Sentiment;
  protected readonly Alignment = Alignment;
}
